import type { JSONContent } from '@tiptap/react';

/**
 * survey-api's mail templates (SR-523): every email the portal sends and the ones staff write, under
 * `mail.templates`. Every template's parts are a subject, a preview text and one body — a block
 * document, the portal's own blocks locked inside it — beside its placeholders and conditions; a survey-scoped one has a variant per survey beside the base.
 * Each variant keeps one draft and its versions: version 0 is the default written in code, a
 * published version is current from its effective date, and the worker resolves the current one
 * at send time (the survey's variant, else the base, else the default).
 */

/** Who wrote a version — null for version 0, or once the account is gone. */
export interface IMailAccount {
  accountId: string;
  email: Nullable<string>;
}

/** What today's rules refuse in a stored version or a body, by target (`parts.text`). */
export interface IMailProblem {
  target: string;
  reason: string;
}

/** The surveys a survey-scoped template has a variant for. */
export type MailSurvey = 'FA' | 'FSP' | 'RIA_FIRM' | 'TEAMS';

/** Built-in groups (`survey`, `account`) hold the portal's own mails, sent on its events; staff
 *  create the `custom` ones — sent in bulk, with an unsubscribe footer in code. */
export type MailTemplateGroup = 'survey' | 'account' | 'custom';

/** Where a custom template can be chosen: the Lists send dialog, a cycle's reminder steps. */
export type MailUsage = 'lists' | 'reminders';

export function isCustomGroup(group: Nullable<MailTemplateGroup> | undefined): boolean {
  return group === 'custom';
}

/** A tag's rules, as survey-api keeps them: at most 10, each 1–40 characters, no comma. */
export const TAG_LIMITS = { count: 10, length: 40 } as const;

/** `GET /admin/mail-templates/tags`: the presets and every tag in use, presets first. */
export interface IMailTagCount {
  tag: string;
  templates: number;
  preset: boolean;
}

/** What only code can produce, a locked block of the body: `systemBlock {id}` in the document. */
export interface IMailSystemBlock {
  id: string;
  label: string;
  description: string;
  /** A required one cannot leave the body. */
  required: boolean;
  /** The block as the server renders it with the template's samples, default conditions and the
   *  current design — sanitized like any preview; absent from an older server. */
  sampleHtml?: Nullable<string>;
  sampleText?: Nullable<string>;
}

/** A version's place in its variant's life. */
export type MailVersionStatus = 'current' | 'scheduled' | 'past' | 'withdrawn' | 'default';

/** One template in the list, with the state of each variant that exists. */
export interface IMailTemplateSummary {
  code: string;
  name: string;
  purpose: string;
  group: MailTemplateGroup;
  surveyScoped: boolean;
  builtin?: boolean;
  archivedAt?: Nullable<string>;
  tags: string[];
  /** A custom template's; empty for a built-in one. */
  usages: MailUsage[];
  variants: {
    survey: Nullable<MailSurvey>;
    version: number;
    status: MailVersionStatus;
    /** When the current version took effect; null for version 0. */
    currentAt?: Nullable<string>;
    /** False: the current version no longer passes today's rules, and the default is sent. */
    valid: boolean;
    scheduledAt: Nullable<string>;
    /** A survey's variant with no current version of its own: its mails go with the base. */
    usesBase?: boolean;
  }[];
}

/** How survey-api formats a staff template's placeholder; `url` goes only into a link's address. */
export type MailPlaceholderKind =
  | 'text'
  | 'date'
  | 'datetime'
  | 'money'
  | 'number'
  | 'percent'
  | 'bool'
  | 'enum'
  | 'multi_enum'
  | 'person'
  | 'url'
  | (string & {});

/** Where a staff template's placeholder comes from, as the picker groups them. */
export type MailPlaceholderGroup =
  | 'This email'
  | 'Record'
  | 'Staff fields'
  | 'Answers'
  | 'Figures'
  | 'Mail'
  | (string & {});

/** What a placeholder stands for, and what the preview fills it with, its words, group, kind and
 *  the list field it reads. `own` marks the email's own placeholders (group "This email"); `quick`,
 *  survey-api's few to offer first, is drawn by nothing here. */
export interface IMailPlaceholder {
  name: string;
  description: string;
  sample: string;
  label?: string;
  group?: MailPlaceholderGroup;
  kind?: MailPlaceholderKind;
  field?: Nullable<string>;
  own?: boolean;
  quick?: boolean;
  /** Its Lists field is hidden from the pickers (SR-462), so none offers it; a pill naming it still
   *  reads, and a version naming it still saves. survey-api's flag, which the Lists catalogue
   *  overrides where it is read. */
  hidden?: boolean;
}

/** A list a `repeat` block runs over — `figures` — and the fields of one item. */
export interface IMailList {
  name: string;
  description: string;
  fields: { name: string; label: string; sample: string }[];
}

/** `GET …/placeholders?survey=`: what a variant's body may use. */
export interface IMailPlaceholderCatalogue {
  placeholders: IMailPlaceholder[];
  conditions: IMailCondition[];
  lists: IMailList[];
}

/** `POST /admin/mail-templates`: a custom template, its code made from the name. */
export interface IMailTemplateCreate {
  name: string;
  purpose: string;
  tags: string[];
  /** At least one. */
  usages: MailUsage[];
}

/** `PATCH /admin/mail-templates/{code}`. */
export type MailTemplatePatch = Partial<IMailTemplateCreate>;

/** One survey variant's and version's sends (`GET …/delivery`). */
export interface IMailDeliveryRow {
  survey: Nullable<MailSurvey>;
  version: number;
  sent: number;
  failed: number;
  skipped: number;
  bounced: number;
  complaints: number;
  unsubscribed: number;
  lastSentAt: Nullable<string>;
}

/** What blocks an archive: the reminder steps that still use the template. */
export interface IMailArchiveBlock {
  steps: { cycle: string; survey: string; stepId: string }[];
}

/** A yes/no a rich part's block can be shown on ("Show only if…"), and its preview default. */
export interface IMailCondition {
  name: string;
  description: string;
  sample: boolean;
}

/** One line with placeholders, or a rich block document. */
export type MailPartKind = 'line' | 'rich';

/** An editable part as the template declares it, in the order the mail shows them. */
export interface IMailPartDefinition {
  key: string;
  label: string;
  kind: MailPartKind;
  required: boolean;
  /** The most characters it takes — a line's text, or a rich part's compiled HTML. */
  max: Nullable<number>;
  help: string;
}

export interface ILinePart {
  text: string;
}

/** `doc` is what the editor edits; `html` is what sends — compiled from it in the browser. */
export interface IRichPart {
  doc: JSONContent;
  html: string;
}

export type MailPart = ILinePart | IRichPart;

export type MailParts = Record<string, MailPart>;

/** The sender's name, the address replies go to ("" for the From address) and the email design
 *  the mail is drawn in (`veritant` unless set). */
export interface IMailSettings {
  fromName: string;
  replyTo: string;
  design: string;
}

/** What a preview, a test, a draft or a publication carries. */
export interface IMailBody {
  parts: MailParts;
  settings: IMailSettings;
}

export interface IMailVersion extends IMailBody {
  version: number;
  survey: Nullable<MailSurvey>;
  /** What changed and why, as the publisher wrote it; "" for none and for version 0. */
  note: string;
  createdAt: Nullable<string>;
  createdBy: Nullable<IMailAccount>;
  effectiveAt: Nullable<string>;
  publishedAt: Nullable<string>;
  withdrawnAt: Nullable<string>;
  status: MailVersionStatus;
  /** Empty normally; on the current version, the default text is sent instead. */
  problems: IMailProblem[];
}

/** The editor's saved state for a variant — no effect until it is published. */
export interface IMailDraft extends IMailBody {
  note: string;
  revision: number;
  /** The version the draft started from. */
  baseVersion: number;
  updatedAt: Nullable<string>;
  updatedBy: Nullable<IMailAccount>;
}

export interface IMailVariant {
  survey: Nullable<MailSurvey>;
  /** What its mails go with now — for a survey's variant without a current version of its own,
   *  the base's (its `survey` null). */
  current: Nullable<IMailVersion>;
  scheduled: Nullable<IMailVersion>;
  draft: Nullable<IMailDraft>;
  /** Newest first; the base's last is version 0. */
  versions: IMailVersion[];
}

export interface IMailTemplate {
  code: string;
  name: string;
  purpose: string;
  group: MailTemplateGroup;
  surveyScoped: boolean;
  parts: IMailPartDefinition[];
  placeholders: IMailPlaceholder[];
  conditions: IMailCondition[];
  /** The lists a `repeat` block runs over — a staff template's. */
  lists?: IMailList[];
  builtin?: boolean;
  archivedAt?: Nullable<string>;
  tags: string[];
  usages: MailUsage[];
  /** The body's locked blocks — a built-in template's. */
  systemBlocks: IMailSystemBlock[];
  /** Whether Mail:Contact names a contact when the support contact is empty. */
  fallbackContact?: boolean;
  /** The base first (`survey: null`); a survey's only once something was saved for it. */
  variants: IMailVariant[];
}

/** Whose copy a preview shows: the account that submitted, the advisor it was submitted for, or the
 *  survey's CC address. */
export type MailPreviewRole = 'submitter' | 'advisor' | 'cc';

/** The mail as the worker would send it, with the placeholders' samples. */
export interface IMailPreview {
  subject: string;
  /** The preview text, filled. */
  preheader: string;
  /** A body fragment with inline styles — shown only in a sandboxed frame. */
  html: string;
  text: string;
}

/** A submitted response a preview can be filled from (`responses.browse`) — or, for a staff
 *  template, a list participant (`participantId`, no response). */
export interface IMailSample {
  responseId?: string;
  participantId?: string;
  participant: string;
  survey: string;
  cycle?: string;
  submittedAt?: string;
}

/** A sent test: the caller's own address. */
export interface IMailTestSent {
  to: string;
}
